import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import './AuthPage.css';
import applaiLogo from '../assets/applai-logo.png';

const SLIDES = [
  {
    icon: '',
    tag: 'Application Tracker',
    title: 'Every application,\none place.',
    body: 'Add jobs manually, paste a link, or drop a PDF — AI fills in company, role, salary, and perks automatically.',
    visual: 'tracker',
  },
  {
    icon: '',
    tag: 'Board View',
    title: 'Drag. Drop.\nDone.',
    body: 'A kanban board groups your applications by status. Drag a card to a new column and the status updates instantly.',
    visual: 'board',
  },
  {
    icon: '',
    tag: 'Schedule',
    title: 'Never miss\nan interview.',
    body: 'Log interviews, exams, and calls on a calendar. Get email reminders the day before and the morning of.',
    visual: 'calendar',
  },
  {
    icon: '',
    tag: 'AI Job Analysis',
    title: 'Know your\nchances.',
    body: 'Pick any application and get an AI-powered breakdown — compensation, growth, fit score, red flags, and what to do next.',
    visual: 'analyze',
  },
  {
    icon: '',
    tag: 'Prepare',
    title: 'Walk in\nready.',
    body: 'Generate a tailored review guide, flashcard deck, and mock assessment for any interview — in seconds.',
    visual: 'prepare',
  },
  {
    icon: '',
    tag: 'Compare',
    title: 'Pick the better\noffer.',
    body: 'Select two applications for a side-by-side AI comparison — scored across 6 dimensions with a clear verdict.',
    visual: 'compare',
  },
];

const TrackerVisual = () => (
  <div className="slide-visual slide-visual-tracker">
    <div className="sv-table-header">
      <span>Company</span><span>Role</span><span>Status</span><span>Stage</span>
    </div>
    {[
      { co: 'Nexora Corp', role: 'Software Engineer', status: 'Interview', statusColor: '#6366F1', statusBg: '#EEF2FF', stage: 'Technical Interview' },
      { co: 'Meridian Bank', role: 'Full Stack Dev', status: 'Screening', statusColor: '#B45309', statusBg: '#FFFBEB', stage: 'HR Screening' },
      { co: 'SwiftCart', role: 'Backend Engineer', status: 'Applied', statusColor: '#1D4ED8', statusBg: '#EFF6FF', stage: 'Applied' },
    ].map((r, i) => (
      <div key={i} className="sv-table-row" style={{ animationDelay: `${i * 0.1}s` }}>
        <span className="sv-company">{r.co}</span>
        <span className="sv-role">{r.role}</span>
        <span className="sv-status" style={{ color: r.statusColor, background: r.statusBg }}>{r.status}</span>
        <span className="sv-stage">{r.stage}</span>
      </div>
    ))}
  </div>
);

const BoardVisual = () => (
  <div className="slide-visual slide-visual-board">
    {[
      { label: 'Applied', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', cards: ['Nexora Corp', 'Globe'] },
      { label: 'Interview', color: '#4338CA', bg: '#EEF2FF', border: '#C7D2FE', cards: ['SwiftCart'] },
      { label: 'Offer', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0', cards: ['PayEase'] },
    ].map((col, ci) => (
      <div key={ci} className="sv-col">
        <div className="sv-col-header" style={{ color: col.color, borderTopColor: col.border }}>
          {col.label} <span style={{ background: col.bg, border: `1px solid ${col.border}` }}>{col.cards.length}</span>
        </div>
        {col.cards.map((c, i) => (
          <div key={i} className="sv-card" style={{ background: col.bg, border: `1px solid ${col.border}`, animationDelay: `${(ci * 2 + i) * 0.08}s` }}>
            <div className="sv-card-pin" style={{ background: col.color }} />
            <div className="sv-card-company">{c}</div>
            <div className="sv-card-role">Software Engineer</div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const CalendarVisual = () => {
  const days = ['S','M','T','W','T','F','S'];
  const cells = Array.from({ length: 35 }, (_, i) => i < 3 ? null : i - 2);
  const events = { 12: { label: 'HR Interview', color: '#6366F1', bg: '#EEF2FF' }, 18: { label: 'Tech Exam', color: '#B45309', bg: '#FFFBEB' }, 24: { label: 'Final Interview', color: '#7E22CE', bg: '#FDF4FF' } };
  return (
    <div className="slide-visual slide-visual-calendar">
      <div className="sv-cal-header">April 2025</div>
      <div className="sv-cal-days">{days.map(d => <span key={d}>{d}</span>)}</div>
      <div className="sv-cal-grid">
        {cells.map((n, i) => (
          <div key={i} className={`sv-cal-cell ${events[n] ? 'sv-cal-has-event' : ''} ${n === 18 ? 'sv-cal-today' : ''}`}>
            {n && <span>{n}</span>}
            {n && events[n] && <div className="sv-cal-event" style={{ color: events[n].color, background: events[n].bg }}>{events[n].label}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const CompareVisual = () => (
  <div className="slide-visual slide-visual-compare">
    <div className="sv-cmp-header">
      <div className="sv-cmp-job sv-cmp-left">
        <div className="sv-cmp-avatar" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>A</div>
        <div><div className="sv-cmp-name">Nexora Corp</div><div className="sv-cmp-score sv-winner">Score: 8.4</div></div>
      </div>
      <div className="sv-cmp-vs">VS</div>
      <div className="sv-cmp-job sv-cmp-right">
        <div><div className="sv-cmp-name">Meridian Bank</div><div className="sv-cmp-score">Score: 6.1</div></div>
        <div className="sv-cmp-avatar" style={{ background: '#F0FDF4', color: '#15803D' }}>G</div>
      </div>
    </div>
    {[
      { label: 'Status', l: 'Offer', r: 'Interview', lBetter: true },
      { label: 'Salary', l: 'PHP 80–100k', r: 'PHP 55–70k', lBetter: true },
      { label: 'Setup', l: 'Hybrid', r: 'Onsite', lBetter: null },
    ].map((row, i) => (
      <div key={i} className="sv-cmp-row">
        <span className="sv-cmp-label">{row.label}</span>
        <span className={`sv-cmp-val ${row.lBetter ? 'sv-cmp-better' : ''}`}>{row.l}</span>
        <span className={`sv-cmp-val ${row.lBetter === false ? 'sv-cmp-better' : ''}`}>{row.r}</span>
      </div>
    ))}
  </div>
);

const AnalyzeVisual = () => (
  <div className="slide-visual slide-visual-analyze">
    <div className="sv-analyze-top">
      <div className="sv-analyze-score">
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="26" fill="none" stroke="#E2E6EE" strokeWidth="6"/>
          <circle cx="32" cy="32" r="26" fill="none" stroke="#10B981" strokeWidth="6"
            strokeDasharray="130 170" strokeLinecap="round" transform="rotate(-90 32 32)"/>
          <text x="32" y="37" textAnchor="middle" fontSize="16" fontWeight="700" fill="#111827">8.2</text>
        </svg>
        <div className="sv-analyze-label">Overall Score</div>
      </div>
      <div className="sv-analyze-info">
        <div className="sv-analyze-headline">"Strong fintech role — clarify equity vesting before accepting"</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
          <span style={{background:'#F0FDF4',color:'#15803D',border:'1px solid #BBF7D0',borderRadius:20,padding:'2px 8px',fontSize:10,fontWeight:700}}>✅ Hybrid setup</span>
          <span style={{background:'#FFFBEB',color:'#B45309',border:'1px solid #FDE68A',borderRadius:20,padding:'2px 8px',fontSize:10,fontWeight:700}}>⚠️ Salary unclear</span>
        </div>
      </div>
    </div>
    {[
      { label: 'Compensation', a: 6, color: '#F59E0B' },
      { label: 'Career Growth', a: 9, color: '#10B981' },
      { label: 'Work-Life Balance', a: 8, color: '#2563EB' },
    ].map((row, i) => (
      <div key={i} className="sv-analyze-row">
        <span className="sv-analyze-row-label">{row.label}</span>
        <div className="sv-analyze-row-bar">
          <div style={{width:`${row.a * 10}%`,background:row.color,height:'100%',borderRadius:3,transition:'width 0.5s'}}/>
        </div>
        <span className="sv-analyze-row-score">{row.a}/10</span>
      </div>
    ))}
  </div>
);

const PrepareVisual = () => (
  <div className="slide-visual slide-visual-prepare">
    <div className="sv-prep-header">Review Guide: HR Interview</div>
    {[
      { topic: 'About the Company', tip: 'Research recent news, funding, product launches' },
      { topic: 'Your Strengths Story', tip: 'Prepare 3 STAR-method examples ready to go' },
      { topic: 'Compensation Talk', tip: 'Know your range: PHP 55k–70k for this market' },
    ].map((item, i) => (
      <div key={i} className="sv-prep-item">
        <div>
          <div className="sv-prep-topic">{item.topic}</div>
          <div className="sv-prep-tip">{item.tip}</div>
        </div>
      </div>
    ))}
    <div className="sv-prep-footer">
      <span className="sv-prep-badge">18 Flashcards</span>
      <span className="sv-prep-badge">12 Quiz Questions</span>
    </div>
  </div>
);

const VISUALS = { tracker: TrackerVisual, board: BoardVisual, calendar: CalendarVisual, analyze: AnalyzeVisual, prepare: PrepareVisual, compare: CompareVisual };

const AuthPage = ({ onBack }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [resetToken, setResetToken] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [resetNewPass, setResetNewPass] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slide, setSlide] = useState(0);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [animating, setAnimating] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('token');
    const em = params.get('email');
    if (tok && em) { setResetToken(tok); setResetEmail(em); setMode('reset'); }
  }, []);
  const timerRef = useRef(null);

  const goToSlide = (idx) => {
    if (animating || idx === slide) return;
    setAnimating(true);
    setTimeout(() => {
      setSlide(idx);
      setAnimating(false);
    }, 220);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setSlide(s => (s + 1) % SLIDES.length);
        setAnimating(false);
      }, 220);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, []);

  const resetTimer = (idx) => {
    clearInterval(timerRef.current);
    goToSlide(idx);
    timerRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setSlide(s => (s + 1) % SLIDES.length);
        setAnimating(false);
      }, 220);
    }, 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = mode === 'login' ? { email: form.email, password: form.password } : form;
      const res = await api.post(endpoint, payload);
      login(res.data.token, res.data.user);
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reset email');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (resetNewPass.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const res = await api.post('/api/auth/reset-password', { token: resetToken, email: resetEmail, password: resetNewPass });
      login(res.data.token, res.data.user);
      toast.success('Password reset! You are now logged in.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. Request a new link.');
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    if (!window.google) { toast.error('Google Sign-In not configured.'); return; }
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          const res = await api.post('/api/auth/google', { credential: response.credential });
          login(res.data.token, res.data.user);
          toast.success('Signed in with Google!');
        } catch { toast.error('Google sign-in failed'); }
      }
    });
    window.google.accounts.id.prompt();
  };

  const current = SLIDES[slide];
  const Visual = VISUALS[current.visual];

  return (
    <div className="auth-root">
      <div className="auth-bg">
        <div className="auth-bg-dots" />
        <div className="auth-bg-blob1" />
        <div className="auth-bg-blob2" />
      </div>

      {/* LEFT PANEL */}
      <div className="auth-left">
        <div className="auth-brand" onClick={onBack} style={{ cursor: 'pointer', userSelect: 'none' }}>
          <img src={applaiLogo} alt="ApplAI" className="auth-logo-img" />
          <span className="auth-brand-text">Appl<span style={{color:"var(--secondary)"}}>AI</span></span>
        </div>

        <div className="auth-slideshow">
          {/* Slide dots / navigation — above the title */}
          <div className="auth-slide-nav">
            {SLIDES.map((s, i) => (
              <button
                key={i}
                className={`slide-dot ${i === slide ? 'active' : ''}`}
                onClick={() => resetTimer(i)}
                aria-label={s.tag}
              />
            ))}
          </div>

          {/* Slide tag */}
          <div className={`slide-tag ${animating ? 'slide-exit' : 'slide-enter'}`}>
            {current.tag}
          </div>

          {/* Main heading */}
          <h1 className={`auth-hero-title ${animating ? 'slide-exit' : 'slide-enter'}`}>
            {current.title.split('\n').map((line, i) => (
              <span key={i}>{line}{i < current.title.split('\n').length - 1 && <br />}</span>
            ))}
          </h1>

          {/* Body text */}
          <p className={`auth-hero-sub ${animating ? 'slide-exit' : 'slide-enter'}`}>
            {current.body}
          </p>

          {/* Learn more button */}
          <button className="auth-learn-more-btn" onClick={() => setShowLearnMore(true)}>
            Learn more about all features
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>

          {/* Visual preview */}
          <div className={`slide-visual-wrap ${animating ? 'slide-exit' : 'slide-enter'}`}>
            <Visual />
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="auth-right">
        <div className="auth-card">
          {/* Brand mark on the form side */}
          <div className="auth-card-brand" onClick={onBack} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <img src={applaiLogo} alt="ApplAI" className="auth-card-logo-img" />
            <span className="auth-card-brand-name">Appl<span style={{color:"var(--secondary)"}}>AI</span></span>
          </div>

          {/* Back button */}
          {onBack && (mode === 'login' || mode === 'register') && (
            <button className="auth-back-btn" onClick={onBack}>
              ← Back to home
            </button>
          )}

          {/* Forgot password */}
          {mode === 'forgot' && (
            <div className="auth-forgot-wrap">
              <div className="auth-forgot-icon"></div>
              <h2 className="auth-card-title">Forgot password?</h2>
              {forgotSent ? (
                <>
                  <p className="auth-card-sub">Check your inbox — we sent a reset link to <strong>{forgotEmail}</strong>.</p>
                  <button className="auth-btn-primary" style={{marginTop:16}} onClick={() => { setMode('login'); setForgotSent(false); }}>Back to Sign In</button>
                </>
              ) : (
                <>
                  <p className="auth-card-sub">Enter your email and we'll send you a reset link.</p>
                  <form className="auth-form" onSubmit={handleForgot} style={{marginTop:16}}>
                    <div className="auth-field">
                      <label>Email</label>
                      <input type="email" placeholder="you@email.com" value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)} required />
                    </div>
                    <button className="auth-btn-primary" type="submit" disabled={loading}>
                      {loading ? <span className="btn-spinner" /> : 'Send Reset Link'}
                    </button>
                  </form>
                  <p className="auth-note" style={{marginTop:14}}>
                    <button className="auth-link-btn" onClick={() => setMode('login')}>← Back to Sign In</button>
                  </p>
                </>
              )}
            </div>
          )}

          {/* Reset password */}
          {mode === 'reset' && (
            <div className="auth-forgot-wrap">
              <div className="auth-forgot-icon"></div>
              <h2 className="auth-card-title">Set new password</h2>
              <p className="auth-card-sub">Choose a strong password for <strong>{resetEmail}</strong>.</p>
              <form className="auth-form" onSubmit={handleReset} style={{marginTop:16}}>
                <div className="auth-field">
                  <label>New Password</label>
                  <div className="auth-pw-wrap">
                    <input type={showResetPassword ? 'text' : 'password'} placeholder="Min. 6 characters" value={resetNewPass}
                      onChange={e => setResetNewPass(e.target.value)} required />
                    <button type="button" className="auth-pw-eye" onClick={() => setShowResetPassword(v => !v)} tabIndex={-1}>
                      {showResetPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <button className="auth-btn-primary" type="submit" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Reset Password & Sign In'}
                </button>
              </form>
            </div>
          )}

          {/* Login / Register */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <h2 className="auth-card-title">
                {mode === 'login' ? 'Welcome back' : 'Create your account'}
              </h2>
              <p className="auth-card-sub">
                {mode === 'login' ? 'Sign in to your ApplAI account' : 'Start your job search smarter'}
              </p>

              <div className="auth-tabs">
                <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>Sign In</button>
                <button className={`auth-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>Register</button>
              </div>

              <form className="auth-form" onSubmit={handleSubmit}>
                {mode === 'register' && (
                  <div className="auth-field">
                    <label>Full Name</label>
                    <input type="text" placeholder="Juan dela Cruz" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })} required />
                  </div>
                )}
                <div className="auth-field">
                  <label>Email</label>
                  <input type="email" placeholder="you@email.com" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="auth-field">
                  <label>Password</label>
                  <div className="auth-pw-wrap">
                    <input type={showPassword ? 'text' : 'password'} placeholder={mode === 'register' ? 'Min. 6 characters' : '••••••••'}
                      value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                    <button type="button" className="auth-pw-eye" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {mode === 'login' && (
                    <button type="button" className="auth-forgot-link" onClick={() => setMode('forgot')}>
                      Forgot password?
                    </button>
                  )}
                </div>
                <button className="auth-btn-primary" type="submit" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              </form>

              <div className="auth-divider"><span>or continue with</span></div>

              <button className="auth-btn-google" onClick={handleGoogle}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <p className="auth-note">
                {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button className="auth-link-btn" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
                  {mode === 'login' ? 'Register' : 'Sign in'}
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      {/* LEARN MORE MODAL */}
      {showLearnMore && <LearnMoreModal onClose={() => setShowLearnMore(false)} />}
    </div>
  );
};

const FEATURES = [
  {
    title: 'AI-Powered Job Parsing',
    desc: 'Paste any job posting URL or upload a PDF — Gemini AI extracts company, role, salary range, work setup, location, and benefits automatically. No manual typing needed.',
  },
  {
    title: 'Application Tracker',
    desc: 'A full-featured table with sorting, filtering by status and stage, and search. Switch to the kanban board view and drag post-it cards between columns to update status instantly.',
  },
  {
    title: 'Interview Calendar & Reminders',
    desc: 'Log HR calls, technical interviews, and assessments on a monthly calendar. Same-day events are highlighted with urgency alerts. Get email reminders the day before and day of.',
  },
  {
    title: 'AI Job Analysis',
    desc: 'Select any application for a deep AI-powered breakdown — overall score, compensation analysis, red flags, career growth prospects, and a "what to do next" recommendation tailored to you.',
  },
  {
    title: 'Personalized Fit Assessment',
    desc: 'After the initial analysis, answer 6–8 dynamically generated questions about your priorities and situation. Get a personal fit score, green lights, watch-outs, and a final verdict.',
  },
  {
    title: 'Interview Preparation',
    desc: 'Generate a tailored review guide, a flashcard deck, and a mock assessment quiz for any scheduled interview — all in one click. Export as PDF to study anywhere.',
  },
  {
    title: 'AI Application Comparison',
    desc: 'Pick two applications and get an AI-scored side-by-side comparison across 6 weighted dimensions: compensation, growth, stability, work-life balance, role fit, and application momentum.',
  },
  {
    title: 'Profile & Qualifications',
    desc: 'Store your education, work history, and skills. ApplAI uses this profile to personalize every analysis — the fit score, qualification match sections, and interview prep become much more accurate.',
  },
  {
    title: 'Linked Job Updates',
    desc: "When logging a calendar event like a Technical Interview, optionally auto-update the linked job's stage and status in one step — keeping everything in sync.",
  },
  {
    title: 'Secure & Private',
    desc: 'Sign up with email/password or Google Sign-In. All your data is private to your account, protected with JWT authentication. Nothing is shared.',
  },
];

const LearnMoreModal = ({ onClose }) => (
  <div className="lm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="lm-panel">
      <div className="lm-header">
        <div>
          <h2 className="lm-title">Everything <span style={{color:"var(--primary)"}}>Appl</span><span style={{color:"var(--secondary)"}}>AI</span> can do</h2>
          <p className="lm-sub">Everything ApplAI does to help you land your next role</p>
        </div>
        <button className="lm-close" onClick={onClose}>✕</button>
      </div>
      <div className="lm-body">
        <div className="lm-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="lm-card">
              <h3 className="lm-card-title">{f.title}</h3>
              <p className="lm-card-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="lm-footer">
        <button className="lm-cta" onClick={onClose}>Start Tracking — Free to Set Up</button>
      </div>
    </div>
  </div>
);

export default AuthPage;
